import styled from 'styled-components'
import { useMemo } from 'react'
import Decimal from 'decimal.js'
import { ClmmPool, DryRunReduceResult, PositionConfigInfo, StructClass, TypeArgument, USDC } from '@kunalabs-io/kai'

import { Typography } from 'components/Typography/Typography'
import { IconArrowRight } from 'components/Icons/IconArrowRight'
import { useTokenPrice } from 'utils/useTokenPrice'
import { ReductionInfoSubmit } from '../../types'
import { formatPercentage, getPercentageChange, SMALL_PCT_ROUNDED } from '../utils'
import { useIsMobile } from 'utils/useIsMobile'
import { PriceSection } from './PriceSection'
import { getFormattedUsdValue } from 'utils/usdFormatters'

const ExpansionPanelTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const WithdrawSummaryContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 65%;
  position: relative;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    width: 75%;
  }
`
const PositionBreakdownContainerItem = styled.div`
  display: flex;
`

const PositionBreakdownSymbol = styled(Typography)`
  display: flex;
  svg {
    margin-right: 8px;
  }
`

interface SwapProps {
  configInfo: PositionConfigInfo<string, string, TypeArgument>
  swapX: Decimal
  swapY: Decimal
  swapXUsd: string
  swapYUsd: string
  percentageChange?: Decimal
  dryRunError?: Error
}

const XToYSwap = ({ configInfo, swapX, swapY, swapXUsd, swapYUsd, percentageChange, dryRunError }: SwapProps) => {
  const isMobile = useIsMobile()
  const formattedPctChange = percentageChange ? formatPercentage(percentageChange) : ''

  return (
    <WithdrawSummaryContainer>
      <div style={{ width: '48%' }}>
        <PositionBreakdownContainerItem>
          <PositionBreakdownSymbol variant={isMobile ? 'caption' : 'description'} fontWeight="semiBold">
            <img
              src={configInfo?.X.iconUrl}
              alt={configInfo?.X.displaySymbol}
              width={isMobile ? 16 : 24}
              height={isMobile ? 16 : 24}
            />
            &nbsp;
          </PositionBreakdownSymbol>
          <Typography variant={isMobile ? 'caption' : 'description'} fontWeight="semiBold">
            {swapX.abs().toString()}
            <Typography variant="caption" color="secondary" style={{ textAlign: 'right' }}>
              {swapXUsd}
            </Typography>
          </Typography>
        </PositionBreakdownContainerItem>
      </div>
      <IconArrowRight />
      <div style={{ width: '48%', textAlign: 'right' }}>
        <div>
          <PositionBreakdownContainerItem style={{ justifyContent: 'flex-end' }}>
            <PositionBreakdownSymbol variant={isMobile ? 'caption' : 'description'} fontWeight="semiBold">
              <img
                src={configInfo?.Y.iconUrl}
                alt={configInfo?.Y.displaySymbol}
                width={isMobile ? 16 : 24}
                height={isMobile ? 16 : 24}
              />
              &nbsp;
            </PositionBreakdownSymbol>
            <Typography variant={isMobile ? 'caption' : 'description'} fontWeight="semiBold">
              {swapY.toString()}
            </Typography>
          </PositionBreakdownContainerItem>
          <Typography variant="caption" color="secondary">
            {swapYUsd}
          </Typography>
        </div>
      </div>
      {percentageChange && formattedPctChange && !dryRunError && (
        <Typography
          variant="caption"
          color={
            formattedPctChange === SMALL_PCT_ROUNDED ? 'success' : percentageChange.isNegative() ? 'danger' : 'success'
          }
          style={{ position: 'absolute', right: isMobile ? -60 : -70 }}
        >
          {formatPercentage(percentageChange)}
        </Typography>
      )}
    </WithdrawSummaryContainer>
  )
}

const YToXSwap = ({ configInfo, swapX, swapY, swapXUsd, swapYUsd, percentageChange, dryRunError }: SwapProps) => {
  const isMobile = useIsMobile()
  const formattedPctChange = percentageChange ? formatPercentage(percentageChange) : ''

  return (
    <WithdrawSummaryContainer>
      <div style={{ width: '48%' }}>
        <PositionBreakdownContainerItem>
          <PositionBreakdownSymbol variant={isMobile ? 'caption' : 'description'} fontWeight="semiBold">
            <img
              src={configInfo?.Y.iconUrl}
              alt={configInfo?.Y.displaySymbol}
              width={isMobile ? 16 : 24}
              height={isMobile ? 16 : 24}
            />
            &nbsp;
          </PositionBreakdownSymbol>
          <Typography variant={isMobile ? 'caption' : 'description'} fontWeight="semiBold">
            {swapY.abs().toString()}
            <Typography variant="caption" color="secondary" style={{ textAlign: 'right' }}>
              {swapYUsd}
            </Typography>
          </Typography>
        </PositionBreakdownContainerItem>
      </div>
      <IconArrowRight />
      <div style={{ width: '48%', textAlign: 'right' }}>
        <div>
          <PositionBreakdownContainerItem style={{ justifyContent: 'flex-end' }}>
            <PositionBreakdownSymbol variant={isMobile ? 'caption' : 'description'} fontWeight="semiBold">
              <img
                src={configInfo?.X.iconUrl}
                alt={configInfo?.X.displaySymbol}
                width={isMobile ? 16 : 24}
                height={isMobile ? 16 : 24}
              />
              &nbsp;
            </PositionBreakdownSymbol>
            <Typography variant={isMobile ? 'caption' : 'description'} fontWeight="semiBold">
              {swapX.toString()}
            </Typography>
          </PositionBreakdownContainerItem>
          <Typography variant="caption" color="secondary">
            {swapXUsd}
          </Typography>
        </div>
      </div>
      {percentageChange && formattedPctChange && !dryRunError && (
        <Typography
          variant="caption"
          color={
            formattedPctChange === SMALL_PCT_ROUNDED ? 'success' : percentageChange.isNegative() ? 'danger' : 'success'
          }
          style={{ position: 'absolute', right: isMobile ? -60 : -70 }}
        >
          {formatPercentage(percentageChange)}
        </Typography>
      )}
    </WithdrawSummaryContainer>
  )
}

interface Props {
  configInfo: PositionConfigInfo<string, string, TypeArgument>
  reductionInfoSubmit: ReductionInfoSubmit
  isReversedPair: boolean
  pool?: ClmmPool<StructClass, unknown, string, string>
  dryRunResult?: DryRunReduceResult
  isCalculatingDryRun: boolean
  dryRunError?: Error
}

const getUsdValueForDisplay = (
  useDryRunValue: boolean,
  reductionInfoValue?: Decimal,
  dryRunValue?: Decimal
): string => {
  if (useDryRunValue) {
    return dryRunValue ? getFormattedUsdValue(dryRunValue.toNumber()) : ''
  }

  return reductionInfoValue ? getFormattedUsdValue(reductionInfoValue.toNumber()) : ''
}

export const WithdrawSummary = ({
  configInfo,
  reductionInfoSubmit,
  isReversedPair,
  pool,
  isCalculatingDryRun,
  dryRunResult,
  dryRunError,
}: Props) => {
  const isMobile = useIsMobile()

  const { isLoading: isLoadingUsdValueX, price: priceX } = useTokenPrice(configInfo.X, USDC)

  const showDryRunResult = !isCalculatingDryRun && dryRunResult

  const { swapXReductionInfoUsd, swapXDryRunUsd } = useMemo(() => {
    if (!priceX || isLoadingUsdValueX) {
      return {
        swapXReductionInfoUsd: undefined,
        swapXDryRunUsd: undefined,
      }
    }

    const swapXReductionInfoUsd = new Decimal(0).plus(reductionInfoSubmit.swapX.mul(priceX.human)).abs()
    const swapXDryRunUsd = showDryRunResult
      ? new Decimal(0).plus(dryRunResult.swapX.toDecimal().mul(priceX.human)).abs()
      : undefined

    return {
      swapXReductionInfoUsd,
      swapXDryRunUsd,
    }
  }, [priceX, isLoadingUsdValueX, showDryRunResult, reductionInfoSubmit.swapX, dryRunResult?.swapX])

  const { isLoading: isLoadingUsdValueY, price: priceY } = useTokenPrice(configInfo.Y, USDC)

  const { swapYReductionInfoUsd, swapYDryRunUsd } = useMemo(() => {
    if (!priceY || isLoadingUsdValueY) {
      return {
        swapYReductionInfoUsd: undefined,
        swapYDryRunUsd: undefined,
      }
    }

    const swapYReductionInfoUsd = new Decimal(0).plus(reductionInfoSubmit.swapY.mul(priceY.human)).abs()
    const swapYDryRunUsd = showDryRunResult
      ? new Decimal(0).plus(dryRunResult.swapY.toDecimal().mul(priceY.human)).abs()
      : undefined

    return {
      swapYReductionInfoUsd,
      swapYDryRunUsd,
    }
  }, [priceY, isLoadingUsdValueY, reductionInfoSubmit.swapY, showDryRunResult, dryRunResult?.swapY])

  let xIsInput
  let yIsInput
  let percentageChange: Decimal | undefined
  if (showDryRunResult) {
    xIsInput = !dryRunResult.swapX.toDecimal().isZero() && dryRunResult.swapX.toDecimal().isNegative()
    yIsInput = !dryRunResult.swapY.toDecimal().isZero() && dryRunResult.swapY.toDecimal().isNegative()
    if (xIsInput && swapXDryRunUsd && swapYDryRunUsd) {
      percentageChange = getPercentageChange(swapXDryRunUsd, swapYDryRunUsd)
    }
    if (yIsInput && swapXDryRunUsd && swapYDryRunUsd) {
      percentageChange = getPercentageChange(swapYDryRunUsd, swapXDryRunUsd)
    }
  } else {
    xIsInput = !reductionInfoSubmit.swapX.isZero() && reductionInfoSubmit.swapX.isNegative()
    yIsInput = !reductionInfoSubmit.swapY.isZero() && reductionInfoSubmit.swapY.isNegative()
  }

  return (
    <ExpansionPanelTitleContainer>
      {xIsInput ? (
        <XToYSwap
          configInfo={configInfo}
          swapX={showDryRunResult ? dryRunResult.swapX.toDecimal() : reductionInfoSubmit.swapX}
          swapY={showDryRunResult ? dryRunResult.swapY.toDecimal() : reductionInfoSubmit.swapY}
          swapXUsd={getUsdValueForDisplay(!!showDryRunResult, swapXReductionInfoUsd, swapXDryRunUsd)}
          swapYUsd={getUsdValueForDisplay(!!showDryRunResult, swapYReductionInfoUsd, swapYDryRunUsd)}
          percentageChange={percentageChange}
          dryRunError={dryRunError}
        />
      ) : yIsInput ? (
        <YToXSwap
          configInfo={configInfo}
          swapX={showDryRunResult ? dryRunResult.swapX.toDecimal() : reductionInfoSubmit.swapX}
          swapY={showDryRunResult ? dryRunResult.swapY.toDecimal() : reductionInfoSubmit.swapY}
          swapXUsd={getUsdValueForDisplay(!!showDryRunResult, swapXReductionInfoUsd, swapXDryRunUsd)}
          swapYUsd={getUsdValueForDisplay(!!showDryRunResult, swapYReductionInfoUsd, swapYDryRunUsd)}
          percentageChange={percentageChange}
          dryRunError={dryRunError}
        />
      ) : null}
      {!isMobile && (
        <div>
          <PriceSection
            tokenXSymbol={isReversedPair ? configInfo?.Y.displaySymbol || '' : configInfo?.X.displaySymbol || ''}
            tokenYSymbol={isReversedPair ? configInfo?.X.displaySymbol || '' : configInfo?.Y.displaySymbol || ''}
            currentPrice={
              isReversedPair
                ? pool?.currentPrice().inverted().human.toDP(2).toString()
                : pool?.currentPrice().human.toDP(2).toString()
            }
          />
        </div>
      )}
    </ExpansionPanelTitleContainer>
  )
}
